import { ReactNode } from "react";
import { Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Link, Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { GovSettingsOn } from "../../../utils/icGovernance";
import { TICKET_VIEW_URL } from "../Support/supportView";
import { GOV_TABS, PRIMARY, clientLabel, vendorLabel, visibleTabs } from "./governanceStatus";
import { INK, MONO, MUTED } from "./registers/cells";

/**
 * The project workbook on the Support page (`/dashboard/support/<tab>`):
 * project header + the workbook tab row around the register routes
 * (`<Outlet/>`). Rendered by `Support/SupportHub` only when the server's
 * `/ic-ticket/governance/me` says `enabled` (admins) — the hub owns loading,
 * errors and the ticket-list fallback. Registers read the settings with
 * `useGovernance()` instead of fetching them again. Item detail and the weekly
 * report page sit outside this layout (their own routes).
 * Contract: thrive_server/claude_doc/governance/CONTRACT.md.
 */

export interface GovernanceContextValue {
  settings: GovSettingsOn;
}

/** The governance settings inside a register (only rendered when enabled). */
export const useGovernance = () => useOutletContext<GovernanceContextValue>();

const GovernancePage = ({ children }: { children: ReactNode }) => (
  <Box sx={{ padding: { xs: "16px", sm: "20px" }, textAlign: "left", minWidth: 0 }}>{children}</Box>
);

const GovernanceLayout = ({ settings }: { settings: GovSettingsOn }) => {
  const { pathname } = useLocation();

  const tabs = visibleTabs(settings);
  const current = GOV_TABS.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`));
  // A tab whose module is off (typed URL, or the Tracker as the default
  // landing tab) goes to the first tab the project uses.
  if (current && !tabs.some((t) => t.key === current.key) && tabs.length > 0) {
    return <Navigate to={tabs[0].path} replace />;
  }

  const context: GovernanceContextValue = { settings };

  return (
    <GovernancePage>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED }}
          >
            Project governance
          </Typography>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: INK, mt: 0.25 }}>
            Thrive
            <Box
              component="span"
              sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color: MUTED, ml: 1 }}
            >
              {settings.projectKey}
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>
            Client: {clientLabel(settings)} · Vendor: {vendorLabel(settings)}
          </Typography>
        </Box>
        {/* The plain Support ticket list stays one click away. */}
        <Button
          component={Link}
          to={TICKET_VIEW_URL}
          size="small"
          sx={{ textTransform: "none", color: MUTED, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Ticket view
        </Button>
      </Stack>

      <Tabs
        value={current?.key ?? false}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Project sections"
        sx={{
          mb: 2.5,
          borderBottom: "1px solid #d9dee6",
          minHeight: 44,
          "& .MuiTabs-indicator": { backgroundColor: PRIMARY, height: 3 },
        }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={t.label}
            component={Link}
            to={t.path}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              minHeight: 44,
              color: "#3f4753",
              "&.Mui-selected": { color: PRIMARY },
            }}
          />
        ))}
      </Tabs>

      <Outlet context={context} />
    </GovernancePage>
  );
};

export default GovernanceLayout;
